import {FC, useEffect, useState} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Datatable} from '../../../components/Datatable/Datatable'
import {Button} from '../../../components/Button/Button'
import {Input} from '../../../components/Input/Input'
import {GET, POST} from '../../../services/api'
import {Loading} from '../../../components/Loading/Loading'
import Swal from 'sweetalert2'
import {BreadCrumbs} from '../../../components/BreadCrumbs/breadCrumbrs'
import * as XLSX from 'sheetjs-style'

type Environment = {
  id: number
  name: string
}

type BookingHour = {
  id: number
  hour: string
}

type AllocationReportData = {
  id: number
  date: string
  time: string
  environment: string
  tableName: string
  customerName: string
  people: number
  seatsPerTable: number | null
  pricePerSeat: number | null
  pricePerTable: number | null
  pricePerEnvironment: number | null
  priceByGroup: any
  calculatedPrice: number
  paymentValue: number
  status: string
  allocatedAt: string
  deallocatedAt: string | null
  hasCapacityConfig: boolean
  hasPriceConfig: boolean
}

const TableAllocationReportContent: FC = () => {
  const [loading, setLoading] = useState(false)
  const [environments, setEnvironments] = useState<Environment[]>([])
  const [bookingHours, setBookingHours] = useState<BookingHour[]>([])
  const [reportData, setReportData] = useState<AllocationReportData[]>([])

  // Filtros
  const [selectedEnvironment, setSelectedEnvironment] = useState<number | string>('all')
  const [filterType, setFilterType] = useState<'date' | 'period' | 'month' | 'year'>('date')
  const [filterDate, setFilterDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  )
  const [filterStartDate, setFilterStartDate] = useState<string>('')
  const [filterEndDate, setFilterEndDate] = useState<string>('')
  const [filterMonth, setFilterMonth] = useState<string>(
    new Date().toISOString().slice(0, 7)
  )
  const [filterYear, setFilterYear] = useState<string>(
    new Date().getFullYear().toString()
  )
  const [selectedHourId, setSelectedHourId] = useState<number | string>('all')

  useEffect(() => {
    loadInitialData()
  }, [])

  const loadInitialData = async () => {
    try {
      setLoading(true)

      // Carregar ambientes
      const envRes = await GET('/queueReservation/bookingEnvironment')
      if (envRes.success && envRes.data) {
        setEnvironments(envRes.data)
      }

      // Carregar horários
      const hoursRes = await GET('/queueReservation/bookingHour')
      if (hoursRes.success && hoursRes.data) {
        setBookingHours(hoursRes.data)
      }

      setLoading(false)
    } catch (e) {
      console.error('Erro ao carregar dados iniciais:', e)
      setLoading(false)
    }
  }

  const applyFilters = async () => {
    try {
      setLoading(true)

      let dateFilter: any = {}

      // Construir filtro de data baseado no tipo selecionado
      switch (filterType) {
        case 'date':
          if (!filterDate) {
            Swal.fire({
              icon: 'warning',
              title: 'Atenção',
              text: 'Por favor, selecione uma data!',
            })
            setLoading(false)
            return
          }
          dateFilter = {
            type: 'date',
            value: filterDate,
          }
          break
        case 'period':
          if (!filterStartDate || !filterEndDate) {
            Swal.fire({
              icon: 'warning',
              title: 'Atenção',
              text: 'Por favor, selecione o período completo!',
            })
            setLoading(false)
            return
          }
          dateFilter = {
            type: 'period',
            startDate: filterStartDate,
            endDate: filterEndDate,
          }
          break
        case 'month':
          if (!filterMonth) {
            Swal.fire({
              icon: 'warning',
              title: 'Atenção',
              text: 'Por favor, selecione um mês!',
            })
            setLoading(false)
            return
          }
          dateFilter = {
            type: 'month',
            value: filterMonth,
          }
          break
        case 'year':
          if (!filterYear) {
            Swal.fire({
              icon: 'warning',
              title: 'Atenção',
              text: 'Por favor, selecione um ano!',
            })
            setLoading(false)
            return
          }
          dateFilter = {
            type: 'year',
            value: filterYear,
          }
          break
      }

      const payload: any = {
        dateFilter,
      }

      if (selectedEnvironment !== 'all') {
        payload.bookingEnvironmentId = Number(selectedEnvironment)
      }

      if (selectedHourId !== 'all') {
        payload.bookingHourId = Number(selectedHourId)
      }

      console.log('📊 Aplicando filtros:', payload)

      const res = await POST('/queueReservation/tableAllocation/report', payload)

      if (res.success && res.data) {
        console.log('📊 Dados recebidos (amostra):', res.data[0]);
        console.log('🔑 Chaves disponíveis:', res.data[0] ? Object.keys(res.data[0]) : 'Nenhum dado');
        
        const formattedData = res.data.map((item: any, index: number) => {
          // Formatar data corretamente
          let formattedDate = '-';
          if (item.date) {
            const dateParts = item.date.split('-');
            if (dateParts.length === 3) {
              formattedDate = `${dateParts[2]}/${dateParts[1]}/${dateParts[0]}`;
            }
          }
          
            // Extrair informações do ambiente (PostgreSQL retorna em lowercase)
            // IMPORTANTE: Valores vêm em CENTAVOS do banco, precisam ser convertidos para REAIS
            const pricePerSeat = parseFloat(item.priceperseat || item.pricePerSeat || 0) / 100;
            const pricePerTable = parseFloat(item.pricepertable || item.pricePerTable || 0) / 100;
            const pricePerEnvironment = parseFloat(item.priceperenvironment || item.pricePerEnvironment || 0) / 100;
            const seatsPerTable = parseInt(item.seatspertable || item.seatsPerTable || 0);
            const customCapacity = parseInt(item.customcapacity || item.customCapacity || 0);
            const priceByGroup = item.pricebygroup || item.priceByGroup || null;
            const people = parseInt(item.people || 0);          if (index === 0) {
            console.log('💰 Valores extraídos (primeira linha):');
            console.log('  - pricePerSeat:', pricePerSeat);
            console.log('  - pricePerTable:', pricePerTable);
            console.log('  - pricePerEnvironment:', pricePerEnvironment);
            console.log('  - seatsPerTable (capacidade da mesa):', seatsPerTable);
            console.log('  - customCapacity:', customCapacity);
            console.log('  - people (pessoas na reserva):', people);
            console.log('🔍 VERIFICAÇÃO:');
            console.log('  - item.seatsPerTable (raw):', item.seatsPerTable, item.seatspertable);
            console.log('  - item.people (raw):', item.people);
          }
          
          // Usar capacidade customizada se disponível, senão usar seatsPerTable
          const tableCapacity = customCapacity > 0 ? customCapacity : (seatsPerTable > 0 ? seatsPerTable : null);
          
          // Calcular valor baseado no tipo de cobrança do ambiente
          let calculatedPrice = 0;
          let priceType = 'Não definido';
          
          if (pricePerSeat > 0 && people > 0) {
            // Cobrança por assento
            calculatedPrice = pricePerSeat * people;
            priceType = 'Por assento';
          } else if (pricePerTable > 0) {
            // Cobrança por mesa
            calculatedPrice = pricePerTable;
            priceType = 'Por mesa';
          } else if (pricePerEnvironment > 0) {
            // Cobrança por ambiente
            calculatedPrice = pricePerEnvironment;
            priceType = 'Por ambiente';
            } else if (priceByGroup && Array.isArray(priceByGroup) && priceByGroup.length > 0) {
              // Cobrança por grupo (faixas de pessoas)
              const groupRange = priceByGroup.find((range: any) => 
                people >= range.minPeople && people <= range.maxPeople
              );
              if (groupRange) {
                // priceByGroup já vem em centavos, precisa converter
                calculatedPrice = parseFloat(groupRange.price || 0) / 100;
                priceType = `Por grupo (${groupRange.minPeople}-${groupRange.maxPeople} pessoas)`;
              }
            }            // Extrair valor do pagamento (se já pago)
            let paymentValue = 0;
            if (item.payment) {
              try {
                const payment = typeof item.payment === 'string' ? JSON.parse(item.payment) : item.payment;
                // Valor do pagamento também está em centavos
                paymentValue = parseFloat(payment?.value || payment?.amount || 0) / 100;
              } catch (e) {
                paymentValue = 0;
              }
            }          // Traduzir status - verificar todos os formatos possíveis
          const statusRaw = (item.status || '').toString().toLowerCase().trim();
          let statusText = 'Pendente';
          
          if (statusRaw === 'active' || statusRaw === 'ativo') {
            statusText = 'Ativo';
          } else if (statusRaw === 'released' || statusRaw === 'liberado') {
            statusText = 'Liberado';
          } else if (statusRaw === 'pending' || statusRaw === 'pendente') {
            statusText = 'Pendente';
          }
          
          if (index === 0) {
            console.log('💰 Cálculo de preço (primeira linha):', {
              pricePerSeat,
              pricePerTable,
              pricePerEnvironment,
              priceByGroup,
              people,
              tableCapacity,
              calculatedPrice,
              priceType,
              statusRaw,
              statusText
            });
          }
          
          return {
            id: item.id,
            date: formattedDate,
            time: item.time || '-',
            environment: item.environment || '-',
            tableName: item.tablename || item.tableName || '-',
            customerName: item.customername || item.customerName || 'Walk-in',
            people: people,
            seatsPerTable: tableCapacity,
            pricePerSeat: pricePerSeat,
            pricePerTable: pricePerTable,
            pricePerEnvironment: pricePerEnvironment,
            priceByGroup: priceByGroup,
            calculatedPrice: calculatedPrice,
            paymentValue: paymentValue,
            status: statusText,
            allocatedAt: item.allocatedat || item.allocatedAt,
            deallocatedAt: item.deallocatedat || item.deallocatedAt || null,
            hasCapacityConfig: tableCapacity !== null && tableCapacity > 0,
            hasPriceConfig: (pricePerSeat > 0 || pricePerTable > 0 || pricePerEnvironment > 0 || (priceByGroup && Array.isArray(priceByGroup) && priceByGroup.length > 0)),
          };
        });

        console.log('📊 Dados formatados (amostra):', formattedData[0]);
        setReportData(formattedData)
        
        if (formattedData.length === 0) {
          Swal.fire({
            icon: 'info',
            title: 'Nenhum registro encontrado',
            text: 'Não foram encontradas alocações para os filtros selecionados.',
          })
        }
      } else {
        setReportData([])
        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: 'Erro ao buscar relatório de alocações!',
        })
      }

      setLoading(false)
    } catch (e) {
      console.error('Erro ao aplicar filtros:', e)
      setLoading(false)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Erro ao buscar relatório de alocações!',
      })
    }
  }

  const clearFilters = () => {
    setSelectedEnvironment('all')
    setFilterType('date')
    setFilterDate(new Date().toISOString().split('T')[0])
    setFilterStartDate('')
    setFilterEndDate('')
    setFilterMonth(new Date().toISOString().slice(0, 7))
    setFilterYear(new Date().getFullYear().toString())
    setSelectedHourId('all')
    setReportData([])
  }

  const exportToCSV = () => {
    if (reportData.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Atenção',
        text: 'Não há dados para exportar!',
      })
      return
    }

    const csvData = reportData.map((row) => ({
      Data: row.date,
      Horário: row.time,
      Ambiente: row.environment,
      Mesa: row.tableName,
      'Capacidade Mesa': row.seatsPerTable ? `${row.seatsPerTable} lugares` : '-',
      Cliente: row.customerName,
      Pessoas: row.people,
      'Valor Calculado': row.calculatedPrice > 0 ? `R$ ${row.calculatedPrice.toFixed(2).replace('.', ',')}` : '-',
      'Valor Pago': row.paymentValue > 0 ? `R$ ${row.paymentValue.toFixed(2).replace('.', ',')}` : 'Não pago',
      Status: row.status,
      'Alocado em': new Date(row.allocatedAt).toLocaleString('pt-BR'),
      'Desalocado em': row.deallocatedAt
        ? new Date(row.deallocatedAt).toLocaleString('pt-BR')
        : '-',
    }))

    const ws = XLSX.utils.json_to_sheet(csvData)
    const csv = XLSX.utils.sheet_to_csv(ws)
    const blob = new Blob(['\uFEFF' + csv], {type: 'text/csv;charset=utf-8;'})
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `relatorio_alocacao_mesas_${new Date().getTime()}.csv`
    link.click()

    Swal.fire({
      icon: 'success',
      title: 'Exportado!',
      text: 'Relatório exportado para CSV com sucesso!',
      timer: 1500,
    })
  }

  const exportToExcel = () => {
    if (reportData.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Atenção',
        text: 'Não há dados para exportar!',
      })
      return
    }

    const excelData = reportData.map((row) => ({
      Data: row.date,
      Horário: row.time,
      Ambiente: row.environment,
      Mesa: row.tableName,
      'Capacidade Mesa': row.seatsPerTable ? `${row.seatsPerTable} lugares` : '-',
      Cliente: row.customerName,
      Pessoas: row.people,
      'Valor Calculado': row.calculatedPrice > 0 ? `R$ ${row.calculatedPrice.toFixed(2).replace('.', ',')}` : '-',
      'Valor Pago': row.paymentValue > 0 ? `R$ ${row.paymentValue.toFixed(2).replace('.', ',')}` : 'Não pago',
      Status: row.status,
      'Alocado em': new Date(row.allocatedAt).toLocaleString('pt-BR'),
      'Desalocado em': row.deallocatedAt
        ? new Date(row.deallocatedAt).toLocaleString('pt-BR')
        : '-',
    }))

    const ws = XLSX.utils.json_to_sheet(excelData)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Alocações')
    XLSX.writeFile(wb, `relatorio_alocacao_mesas_${new Date().getTime()}.xlsx`)

    Swal.fire({
      icon: 'success',
      title: 'Exportado!',
      text: 'Relatório exportado para Excel com sucesso!',
      timer: 1500,
    })
  }

  const exportToPDF = () => {
    Swal.fire({
      icon: 'info',
      title: 'Exportação PDF',
      text: 'Use os formatos CSV ou Excel para exportar os dados. A exportação PDF será implementada em breve.',
    })
  }

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>
                  Relatório de Alocação de Mesas
                </span>
                <BreadCrumbs />
              </h3>
            </div>

            <div className='card-body'>
              {/* Seção de Filtros */}
              <div className='mb-8'>
                <h5 className='fw-bolder mb-4'>Filtros</h5>

                {/* Linha 1: Ambiente e Horário */}
                <div className='row g-4 mb-4'>
                  <div className='col-md-6'>
                    <Input
                      inputAttr={{type: 'select'}}
                      label='Ambiente'
                      value={selectedEnvironment}
                      change={setSelectedEnvironment}
                      options={[
                        {select: 'Todos os Ambientes', value: 'all'},
                        ...environments.map((env) => ({
                          select: env.name,
                          value: env.id,
                        })),
                      ]}
                    />
                  </div>
                  <div className='col-md-6'>
                    <Input
                      inputAttr={{type: 'select'}}
                      label='Horário'
                      value={selectedHourId}
                      change={setSelectedHourId}
                      options={[
                        {select: 'Todos os Horários', value: 'all'},
                        ...bookingHours.map((hour) => ({
                          select: hour.hour,
                          value: hour.id,
                        })),
                      ]}
                    />
                  </div>
                </div>

                {/* Linha 2: Tipo de Filtro de Data */}
                <div className='row g-4 mb-4'>
                  <div className='col-12'>
                    <label className='form-label fw-bolder'>Tipo de Período</label>
                    <div className='d-flex gap-3 flex-wrap'>
                      <div className='form-check'>
                        <input
                          className='form-check-input'
                          type='radio'
                          name='filterType'
                          id='filterDate'
                          checked={filterType === 'date'}
                          onChange={() => setFilterType('date')}
                        />
                        <label className='form-check-label' htmlFor='filterDate'>
                          Data Específica
                        </label>
                      </div>
                      <div className='form-check'>
                        <input
                          className='form-check-input'
                          type='radio'
                          name='filterType'
                          id='filterPeriod'
                          checked={filterType === 'period'}
                          onChange={() => setFilterType('period')}
                        />
                        <label className='form-check-label' htmlFor='filterPeriod'>
                          Período
                        </label>
                      </div>
                      <div className='form-check'>
                        <input
                          className='form-check-input'
                          type='radio'
                          name='filterType'
                          id='filterMonth'
                          checked={filterType === 'month'}
                          onChange={() => setFilterType('month')}
                        />
                        <label className='form-check-label' htmlFor='filterMonth'>
                          Mês
                        </label>
                      </div>
                      <div className='form-check'>
                        <input
                          className='form-check-input'
                          type='radio'
                          name='filterType'
                          id='filterYear'
                          checked={filterType === 'year'}
                          onChange={() => setFilterType('year')}
                        />
                        <label className='form-check-label' htmlFor='filterYear'>
                          Ano
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Linha 3: Campos de Data Condicionais */}
                <div className='row g-4 mb-4'>
                  {filterType === 'date' && (
                    <div className='col-md-4'>
                      <Input
                        inputAttr={{type: 'date'}}
                        label='Data'
                        value={filterDate}
                        change={setFilterDate}
                      />
                    </div>
                  )}

                  {filterType === 'period' && (
                    <>
                      <div className='col-md-3'>
                        <Input
                          inputAttr={{type: 'date'}}
                          label='Data Início'
                          value={filterStartDate}
                          change={setFilterStartDate}
                        />
                      </div>
                      <div className='col-md-3'>
                        <Input
                          inputAttr={{type: 'date'}}
                          label='Data Fim'
                          value={filterEndDate}
                          change={setFilterEndDate}
                        />
                      </div>
                    </>
                  )}

                  {filterType === 'month' && (
                    <div className='col-md-4'>
                      <Input
                        inputAttr={{type: 'month'}}
                        label='Mês'
                        value={filterMonth}
                        change={setFilterMonth}
                      />
                    </div>
                  )}

                  {filterType === 'year' && (
                    <div className='col-md-4'>
                      <Input
                        inputAttr={{type: 'number', min: 2020, max: 2100}}
                        label='Ano'
                        value={filterYear}
                        change={setFilterYear}
                      />
                    </div>
                  )}
                </div>

                {/* Botões de Ação */}
                <div className='d-flex gap-3 flex-wrap'>
                  <Button
                    text='Aplicar Filtros'
                    color='primary'
                    icon='fas fa-filter'
                    iconColor='#FFF'
                    size='small'
                    horizontalPadding={6}
                    btnAttr={{type: 'button'}}
                    click={applyFilters}
                  />
                  <Button
                    text='Limpar Filtros'
                    color='secondary'
                    icon='fas fa-eraser'
                    iconColor='#FFF'
                    size='small'
                    horizontalPadding={6}
                    btnAttr={{type: 'button'}}
                    click={clearFilters}
                  />
                </div>
              </div>

              <hr className='mb-8' />

              {/* Seção de Exportação */}
              {reportData.length > 0 && (
                <div className='mb-6'>
                  <h5 className='fw-bolder mb-4'>
                    Exportar Relatório ({reportData.length} registros)
                  </h5>
                  <div className='d-flex gap-3 flex-wrap'>
                    <Button
                      text='Exportar CSV'
                      color='success'
                      icon='fas fa-file-csv'
                      iconColor='#FFF'
                      size='small'
                      horizontalPadding={6}
                      btnAttr={{type: 'button'}}
                      click={exportToCSV}
                    />
                    <Button
                      text='Exportar Excel'
                      color='success'
                      icon='fas fa-file-excel'
                      iconColor='#FFF'
                      size='small'
                      horizontalPadding={6}
                      btnAttr={{type: 'button'}}
                      click={exportToExcel}
                    />
                    <Button
                      text='Exportar PDF'
                      color='danger'
                      icon='fas fa-file-pdf'
                      iconColor='#FFF'
                      size='small'
                      horizontalPadding={6}
                      btnAttr={{type: 'button'}}
                      click={exportToPDF}
                    />
                  </div>
                </div>
              )}

              {/* Tabela de Resultados */}
              {reportData.length > 0 ? (
                <Datatable
                  data={reportData.map(item => ({
                    ...item,
                    seatsPerTableFormatted: item.seatsPerTable ? `${item.seatsPerTable} lugares` : '-',
                    calculatedPriceFormatted: item.calculatedPrice > 0 
                      ? `R$ ${item.calculatedPrice.toFixed(2).replace('.', ',')}` 
                      : '-',
                    paymentValueFormatted: item.paymentValue > 0 
                      ? `R$ ${item.paymentValue.toFixed(2).replace('.', ',')}` 
                      : 'Não pago',
                  }))}
                  headers={[
                    {key: 'Data', value: 'date', type: 'default'},
                    {key: 'Horário', value: 'time', type: 'default'},
                    {key: 'Ambiente', value: 'environment', type: 'default'},
                    {key: 'Mesa', value: 'tableName', type: 'default'},
                    {key: 'Capacidade', value: 'seatsPerTableFormatted', type: 'default'},
                    {key: 'Cliente', value: 'customerName', type: 'default'},
                    {key: 'Pessoas', value: 'people', type: 'default'},
                    {key: 'Valor Calculado', value: 'calculatedPriceFormatted', type: 'default'},
                    {key: 'Valor Pago', value: 'paymentValueFormatted', type: 'default'},
                    {
                      key: 'Status',
                      value: 'status',
                      type: 'badge',
                      badge: {
                        Ativo: 'success',
                        Liberado: 'secondary',
                        Pendente: 'warning',
                      },
                    },
                    {key: 'Alocado em', value: 'allocatedAt', type: 'datetime'},
                    {key: 'Desalocado em', value: 'deallocatedAt', type: 'datetime'},
                  ]}
                  color='primary'
                  search={true}
                />
              ) : (
                <div className='alert alert-info d-flex align-items-center'>
                  <i className='fas fa-info-circle fs-2 me-3'></i>
                  <div>
                    <h5 className='mb-1'>Nenhum registro encontrado</h5>
                    <p className='mb-0'>
                      Use os filtros acima para buscar alocações de mesas.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

const TableAllocationReport: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: 'Dashboard', path: 'dashboard', isActive: true},
          {
            title: 'Relatório de Alocação',
            path: 'booking/table-allocation-report',
            isActive: true,
          },
        ]}
      >
        Relatório de Alocação de Mesas
      </PageTitle>
      <TableAllocationReportContent />
    </>
  )
}

export {TableAllocationReport}
